import { Component, DestroyRef, inject, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormControl } from '@angular/forms';

import {
  RdsButtonModule,
  RdsIconsModule,
  RdsFormFieldModule,
  RdsProgressSpinnerComponent,
  RdsDrawerActionsDirective,
  RdsAutocompleteComponent,
} from '@rds/angular-components';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DetailedViewObjectService } from '../../services/detailed-view-object.service';
import { AudienceService, CreateUserAudienceRequest } from '../../services/audience.service';
import { AudienceRaw } from '../../models/audience.model';

export type AudienceFormValidity = "valid" | "warning" | "invalid_priority" | "required" | "delete" | "error";

type AudienceForm = FormGroup<{
  type: FormControl<AudienceType | null>;
  specialist: FormControl<Specialist | null>;
  description: FormControl<string | null>;
  color_code: FormControl<string | null>;
}>;

interface AudienceType {
  value: string;
  label: string;
  icon: string;
}

interface Specialist {
  value: string;
  label: string;
}

@Component({
  selector: 'app-audience-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RdsButtonModule,
    RdsIconsModule,
    RdsFormFieldModule,
    RdsProgressSpinnerComponent,
    RdsDrawerActionsDirective,
    RdsAutocompleteComponent
],
  templateUrl: './audience-form.component.html',
  styleUrls: ['./audience-form.component.scss']
})
export class AudienceFormComponent implements OnInit {
  @Input() selectedAudience: AudienceRaw | null = null;
  @Input() isLoadingChanges = false;
  @Output() submitAudience = new EventEmitter<{data: AudienceRaw, valid: AudienceFormValidity}>();
  @Output() deleteAudience = new EventEmitter<AudienceRaw>();
  @Output() cancelForm = new EventEmitter<void>();

  private readonly detailedViewObjectService = inject(DetailedViewObjectService);
  private readonly audienceService = inject(AudienceService);
  private readonly destroyRef = inject(DestroyRef);

  audienceForm!: AudienceForm;
  showSpecialistField = false;

  // Audience and specialist options are provided by backend (Veeva). Start empty and populate from API.
  audienceTypes: AudienceType[] = [];
  specialists: Specialist[] = [];

  // Veeva-loaded lists with full objects
  private veevaAudiences: { id: number; value: string; label: string }[] = [];
  private veevaSpecialists: { id: number; value: string; label: string }[] = [];

  // Different color palette for audiences
  readonly colorOptions = [
    '#6366F1', // Indigo
    '#8B5CF6', // Violet
    '#EC4899', // Pink
    '#EF4444', // Red
    '#F97316', // Orange
    '#EAB308', // Yellow
    '#22C55E', // Green
    '#10B981', // Emerald
    '#06B6D4', // Cyan
    '#3B82F6'  // Blue
  ];

  constructor(private fb: FormBuilder) {
    // Initialize form early to avoid timing issues
    this.setupForm();
  }

  ngOnInit() {
    this.subscribeToDetailedObject();
    this.loadVeevaLists();
  }

  private loadVeevaLists() {
    // Fetch audiences and specialists; fall back to hardcoded on failure
    this.audienceService.getVeevaAudiences(true).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (list) => {
        this.veevaAudiences = (Array.isArray(list) ? list.map(a => ({ id: a.id, value: a.name, label: a.label })) : []);
        // If backend returned audiences, use them as the dropdown options directly
        if (this.veevaAudiences.length > 0) {
          this.audienceTypes = this.veevaAudiences.map(v => ({
            value: v.value,
            label: v.label,
            icon: 'user_add'
          }));
          
          // Update form with loaded audience types if form exists
          this.updateFormWithLoadedData();
        } else {
          // No audiences returned - show empty list (do not fallback to mocked values)
          this.audienceTypes = [];
        }
      },
      error: (err) => {
        console.warn('Failed to load Veeva audiences, using defaults', err);
      }
    });

    this.audienceService.getVeevaSpecialists({ active: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (list) => {
        this.veevaSpecialists = (Array.isArray(list) ? list.map(s => ({ id: s.id, value: s.name, label: s.label })) : []);
        // Use Veeva-provided specialists; if none returned, leave specialists empty
        this.specialists = this.veevaSpecialists.length > 0 ? this.veevaSpecialists.map(s => ({ value: s.value, label: s.label })) : [];
        
        // Update form with loaded specialists if form exists
        this.updateFormWithLoadedData();
      },
      error: (err) => {
        console.warn('Failed to load Veeva specialists, using defaults', err);
      }
    });
  }

  private updateFormWithLoadedData() {
    if (!this.audienceForm || !this.selectedAudience) {
      return;
    }

    const audience = this.selectedAudience;
    const isNew = !audience || audience.id === 0;
    
    if (isNew) {
      return;
    }

    console.log('Updating form with loaded data:', {
      audience_type: audience.audience_type,
      audience_name: audience.name,
      availableTypes: this.audienceTypes,
      availableSpecialists: this.specialists
    });

    let selectedType: AudienceType | null = null;
    let selectedSpecialist: Specialist | null = null;

    if (audience.audience_type === 'specialist') {
      // Find the specialist type from available options
      selectedType = this.audienceTypes.find(t => 
        (t.value || '').toLowerCase().includes('specialist') ||
        (t.label || '').toLowerCase().includes('specialist')
      ) || null;
      
      console.log('Looking for specialist type, found:', selectedType);
      
      // Find the matching specialist by the audience title/name
      selectedSpecialist = this.specialists.find(s => 
        s.label === audience.name || s.value === audience.name
      ) || null;
      
      console.log('Looking for specialist match, found:', selectedSpecialist);
    } else {
      // For non-specialist audiences, find by name or label
      selectedType = this.audienceTypes.find(t => {
        const v = (t.value || '').toString().toLowerCase();
        const l = (t.label || '').toString().toLowerCase();
        const audienceName = (audience.name || '').toString().toLowerCase();
        return v === audienceName || l === audienceName;
      }) || null;
      
      console.log('Looking for non-specialist type, found:', selectedType);
    }

    // Update form values
    this.audienceForm.patchValue({
      type: selectedType,
      specialist: selectedSpecialist
    });
    
    // Trigger type change to show/hide specialist field
    if (selectedType) {
      this.audienceForm.get('type')?.updateValueAndValidity();
    }
  }

  private subscribeToDetailedObject() {
    this.detailedViewObjectService.detailedObject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((obj: any) => {
        if (obj instanceof AudienceRaw) {
          this.selectedAudience = obj;
          this.setupForm();
        }
      });
  }

  private subscribeToTypeChanges() {
    this.audienceForm?.get('type')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((type: any) => {
        // Handle both string values and object values
        let typeValue = '';
        if (typeof type === 'string') {
          typeValue = type;
        } else if (type && typeof type === 'object') {
          typeValue = type.value || type.label || '';
        }
        
        this.showSpecialistField = typeValue.toLowerCase() === 'specialist__c';
        
        const specialistControl = this.audienceForm.get('specialist');
        if (this.showSpecialistField) {
          specialistControl?.setValidators(Validators.required);
        } else {
          specialistControl?.clearValidators();
          specialistControl?.setValue(null);
        }
        specialistControl?.updateValueAndValidity();
      });
  }

  private setupForm() {
    const isNew = !this.selectedAudience || this.selectedAudience.id === 0;
    const audience = this.selectedAudience || new AudienceRaw();
    
    let selectedType: AudienceType | null = null;
    let selectedSpecialist: Specialist | null = null;
    
    if (!isNew && audience.name) {
      // For existing audiences, we need to determine if it's a specialist
      // Check if this is a specialist audience by looking at the API data structure
      // Specialist audiences typically have format "Specialist Name" and audience_type="specialist"
      const isSpecialist = audience.audience_type === 'specialist';
      
      if (isSpecialist) {
        // Find the specialist__c type
        selectedType = this.audienceTypes.find(t => 
          (t.value || '').toLowerCase() === 'specialist__c'
        ) || null;
        
        // Find the matching specialist by the audience title/name
        selectedSpecialist = this.specialists.find(s => 
          s.label === audience.name || s.value === audience.name
        ) || null;
      } else {
        // For non-specialist audiences, find by name or label
        selectedType = this.audienceTypes.find(t => {
          const v = (t.value || '').toString().toLowerCase();
          const l = (t.label || '').toString().toLowerCase();
          const audienceName = (audience.name || '').toString().toLowerCase();
          return v === audienceName || l === audienceName;
        }) || null;
      }
    }
    
    this.audienceForm = this.fb.group({
      type: [selectedType, Validators.required],
      specialist: [selectedSpecialist],
      description: [audience.description || ''],
      color_code: [audience.color_code || '#6366F1', Validators.required]
    }) as AudienceForm;

    // Set up type change subscription after form is created
    this.subscribeToTypeChanges();
    
    // Trigger the type change to show/hide specialist field correctly
    if (selectedType) {
      this.audienceForm.get('type')?.updateValueAndValidity();
    }
  }

  isSpecialistTypeSelected(): boolean {
    const type = this.audienceForm?.get('type')?.value;
    return (type?.value || '').toLowerCase() === 'specialist';
  }

  selectColor(color: string) {
    this.audienceForm.patchValue({ color_code: color });
  }

  onSubmit() {
    if (this.audienceForm.valid) {
      const formValue = this.audienceForm.value;
      
      // Build the title based on type and specialist
      let title = formValue.type?.label || '';
      if (formValue.type?.value === 'specialist__c' && formValue.specialist) {
        title = `${formValue.specialist.label}`;
      }

      // Determine if this is specialist or audience type
      const isSpecialist = formValue.type?.value === 'specialist__c';
      
      // Find the corresponding Veeva IDs
      const veevaAudience = this.veevaAudiences.find(a => a.value === formValue.type?.value);
      const veevaSpecialist = formValue.specialist ? this.veevaSpecialists.find(s => s.value === formValue.specialist?.value) : null;

      // Prepare the request payload
      const request: CreateUserAudienceRequest = {
        title: title,
        description: formValue.description || '',
        color_code: formValue.color_code || '#6366F1',
        audience_type: isSpecialist ? 'specialist' : 'audience',
        veeva_audience_id: isSpecialist ? null : veevaAudience?.id || null,
        veeva_specialist_id: isSpecialist && veevaSpecialist ? veevaSpecialist.id : null
      };

      // Set loading state
      this.isLoadingChanges = true;

      // Determine if this is an update or create operation
      const isUpdate = this.selectedAudience && this.selectedAudience.id !== 0;
      const apiCall = isUpdate 
        ? this.audienceService.updateUserAudience(this.selectedAudience!.id, request)
        : this.audienceService.createUserAudience(request);

      // Make the API call
      apiCall
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.isLoadingChanges = false;
            
            // Trigger refresh of audience table
            this.audienceService.triggerRefresh();
            
            // Create AudienceRaw object for compatibility with existing code
            const savedAudience = Object.assign(new AudienceRaw(), {
              id: response.id,
              name: response.title,
              description: response.description,
              color_code: response.color_code,
              created_at: response.created_at,
              updated_at: response.updated_at
            });

            this.submitAudience.emit({
              data: savedAudience,
              valid: 'valid'
            });
          },
          error: (error) => {
            this.isLoadingChanges = false;
            console.error('Failed to save audience:', error);
            
            this.submitAudience.emit({
              data: this.selectedAudience || new AudienceRaw(),
              valid: 'error'
            });
          }
        });
    } else {
      this.audienceForm.markAllAsTouched();
      this.submitAudience.emit({
        data: this.selectedAudience || new AudienceRaw(),
        valid: 'required'
      });
    }
  }

  onDelete() {
    if (this.selectedAudience && this.selectedAudience.id !== 0) {
      this.deleteAudience.emit(this.selectedAudience);
    }
  }

  closeForm() {
    this.cancelForm.emit();
  }
}
